import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { createTheme, ThemeProvider } from "@vitalsource/vstui/themes";
import { IntlProvider } from "react-intl";
import { cheddar, ocean } from "@vitalsource/vstui/colors";
import { colors } from "chameleonjs";
import BrandProvider from "../brand-context/BrandProvider";
import { LoadStatus, useInit } from "./useInit";
import { removeStaticLoader } from "./utils";
import Errors from "components/Errors";

/**
 * This needs to be a child of the BrandProvider in order to have access to brand data
 * @param children
 * @param onError
 * @constructor
 */
const BaseConfigProviders: FunctionComponent = ({ children }) => {
  const initData = useInit();
  const [error, setError] = useState(false);

  useEffect(() => {
    if (initData.status !== LoadStatus.LOADING) {
      removeStaticLoader();
    }
    if (initData.status === LoadStatus.ERROR) {
      setError(true);
    }
  }, [initData]);

  const vstuiTheme = useMemo(() => {
    if (initData.data) {
      const theme = initData.data.brandData.Theme;
      return createTheme({
        typography: {
          htmlFontSize: 10,
        },
        color: {
          primary: theme ? colors[theme.primary] : ocean,
          secondary: theme ? colors[theme.secondary] : cheddar,
        },
      });
    }
  }, [initData]);

  if (error) {
    return (
      <IntlProvider locale="en">
        <Errors />
      </IntlProvider>
    );
  }

  if (initData.status === LoadStatus.OK && initData.data) {
    return (
      <BrandProvider brandData={initData.data.brandData}>
        <ThemeProvider theme={vstuiTheme}>
          <IntlProvider
            locale={initData.data.locale}
            messages={initData.data.messages}
          >
            {children}
          </IntlProvider>
        </ThemeProvider>
      </BrandProvider>
    );
  }

  return null;
};

export default BaseConfigProviders;
